// Provider setup content — every step mirrors the provider's official help article (linked).
export const PROVIDER_GUIDES = {
  google: {
    oauthTitle: 'Connect with Google (recommended)',
    oauthText: 'Sign in with Google and approve read-only access ("See and download any calendar you can access"). SpaceCal reads events from your primary calendar for the next 60 days and never edits them. Disconnecting revokes access.',
    linkTitle: 'Or subscribe with your secret iCal address',
    linkSource: { label: 'Google Calendar Help', url: 'https://support.google.com/calendar/answer/37648' },
    linkSteps: [
      'On a computer, open Google Calendar (the mobile app can\'t do this).',
      'Top right, click Settings (gear) → Settings.',
      'On the left, under "Settings for my calendars", click the calendar you want.',
      'Scroll to "Integrate calendar" and copy the "Secret address in iCal format".',
      'Paste it below. Keep it private — anyone with it can see your events.',
    ],
    linkNotes: [
      'To revoke the link later, click Reset next to the secret address in Google Calendar.',
      'Work or school account? If you don\'t see the secret address, your administrator has turned it off.',
    ],
    linkPlaceholder: 'https://calendar.google.com/calendar/ical/…/basic.ics',
    fileTitle: 'Or upload a one-time export',
    fileSource: { label: 'Export from Google Calendar', url: 'https://support.google.com/calendar/answer/37111' },
    fileSteps: [
      'On a computer, open Google Calendar → Settings (gear) → Settings.',
      'Click "Import & export" → Export. A ZIP file downloads.',
      'Unzip it and upload the .ics file for the calendar you want below.',
    ],
  },
  microsoft: {
    oauthTitle: 'Connect with Microsoft (recommended)',
    oauthText: 'Sign in with your Microsoft account (Outlook.com, Hotmail, Live) or work/school Microsoft 365 account and approve "Read your calendars". SpaceCal reads your calendar for the next 60 days via Microsoft Graph and never edits it. Some organisations require an admin to approve new apps.',
    linkTitle: 'Or subscribe with a published calendar link',
    linkSource: { label: 'Microsoft Support: Share your calendar in Outlook on the web', url: 'https://support.microsoft.com/en-us/outlook/share-your-calendar-in-outlook-on-the-web' },
    linkSteps: [
      'In Outlook on the web, go to Calendar, open Settings, then select Shared calendars.',
      'Under "Publish a calendar", choose the calendar and how much detail to show.',
      'Select Publish. Two links appear — copy the ICS link (not the HTML link).',
      'Paste it below.',
    ],
    linkNotes: [
      'Anyone with a published link can view the calendar. Choose Unpublish in the same place to stop sharing.',
      'Work or school account? If "Publish a calendar" is missing, your administrator has disabled it — use Connect with Microsoft instead.',
    ],
    linkPlaceholder: 'https://outlook.office365.com/owa/calendar/…/calendar.ics',
  },
  apple: {
    oauthTitle: null,
    oauthNote: 'Apple doesn\'t offer a sign-in (OAuth) calendar API that web apps can use, so iCloud Calendar connects through Apple\'s official Public Calendar link.',
    linkTitle: 'Subscribe with an iCloud public calendar link',
    linkSource: { label: 'iCloud User Guide: Share a calendar', url: 'https://support.apple.com/guide/icloud/share-a-calendar-mm6b1a9479/icloud' },
    linkSteps: [
      'Go to icloud.com/calendar and sign in with your Apple Account.',
      'In the sidebar, select the calendar (hover over it), then click the Share icon.',
      'Turn on Public Calendar, then click Copy Link.',
      'Paste the link (it starts with webcal://) below.',
    ],
    linkNotes: [
      'Anyone with a public calendar link can view it. Turn off Public Calendar in iCloud to stop sharing.',
    ],
    linkPlaceholder: 'webcal://p…-caldav.icloud.com/published/2/…',
    fileTitle: 'Or upload a one-time export from Calendar on Mac',
    fileSource: { label: 'Calendar User Guide for Mac', url: 'https://support.apple.com/guide/calendar/import-or-export-calendars-icl1023/mac' },
    fileSteps: [
      'In the Calendar app on your Mac, click the calendar\'s name in the calendar list (View → Show Calendar List if hidden).',
      'Choose File → Export → Export, pick where to save it, then click Export.',
      'Upload the saved .ics file below.',
    ],
  },
};

export const OWNER_SETUP = {
  google: {
    title: 'App owner setup (Google Cloud Console)',
    source: { label: 'Google: Using OAuth 2.0 for Web Server Applications', url: 'https://developers.google.com/identity/protocols/oauth2/web-server' },
    steps: [
      'In Google Cloud Console, create or select a project and enable the Google Calendar API.',
      'Configure the OAuth consent screen and add the scope …/auth/calendar.readonly (a sensitive scope — Google verification is needed before public launch).',
      'Create an OAuth client ID of type "Web application" and add the redirect URI below.',
      'Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the backend environment.',
    ],
  },
  microsoft: {
    title: 'App owner setup (Microsoft Entra admin center)',
    source: { label: 'Microsoft identity platform: Register an application', url: 'https://learn.microsoft.com/en-us/entra/identity-platform/quickstart-register-app' },
    steps: [
      'In the Microsoft Entra admin center, App registrations → New registration. Supported accounts: "Any Entra ID tenant + personal Microsoft accounts".',
      'Add a Web platform redirect URI (below).',
      'API permissions → Microsoft Graph → Delegated: Calendars.Read, User.Read, offline_access.',
      'Certificates & secrets → New client secret. Add MICROSOFT_CLIENT_ID (Application ID) and MICROSOFT_CLIENT_SECRET to the backend environment.',
    ],
  },
};
